import React, {
  createContext, useContext, useEffect, useMemo, useRef,
} from 'react';
import { useAuth } from './AuthContext';

/**
 * Live refresh — open screens update themselves when somebody else changes
 * what they show, with no page reload.
 *
 * ONE small request per browser tab, whatever is on screen: GET /api/live
 * returns a counter per topic (api/utils/liveUpdates.js):
 *
 *   agency         bookings, requests, offers, connections, agency staff
 *   rota           shifts and clocking, teams, patterns, leave
 *   notifications  this person's notifications (the bell)
 *
 * When a counter moves, every screen subscribed to that topic reloads ITSELF
 * quietly (useLiveRefresh below) — no spinner, filters, tabs, scroll position,
 * open dialogs and half-typed forms all stay as they are.
 *
 * Cadence: every 4 s while the tab is in front; every 30 s while it is in the
 * background (browsers slow hidden tabs anyway); and immediately when the tab
 * comes back into view, the window regains focus or the connection returns —
 * so a tab left open all day is current the moment anyone looks at it.
 *
 * Deliberately plain `fetch`, not axiosInstance: a hiccup here must never sign
 * anybody out. A failed check is silent and tried again next time.
 */

const API = import.meta.env.VITE_API_URL || 'http://localhost:5000/api';
const VISIBLE_MS = 4000;
const HIDDEN_MS = 30000;

const LiveUpdatesContext = createContext(null);

export const LiveUpdatesProvider = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const listeners = useRef(new Set());
  const api = useMemo(() => ({
    subscribe: (fn) => {
      listeners.current.add(fn);
      return () => listeners.current.delete(fn);
    },
  }), []);

  useEffect(() => {
    if (!isAuthenticated) return undefined;
    let stopped = false;
    let timer = null;
    let inFlight = false;
    let token = null;
    let last = null; // counters from the previous check

    const schedule = () => {
      if (stopped) return;
      clearTimeout(timer);
      timer = setTimeout(check, document.hidden ? HIDDEN_MS : VISIBLE_MS); // eslint-disable-line no-use-before-define
    };

    async function check() {
      if (stopped || inFlight) return;
      const current = localStorage.getItem('accessToken');
      if (!current) { schedule(); return; }
      // A new token (site switch, fresh sign-in) is a different set of
      // counters — take a new baseline rather than reporting every topic.
      if (current !== token) { token = current; last = null; }
      inFlight = true;
      try {
        const res = await fetch(`${API}/live`, { headers: { Authorization: `Bearer ${current}` }, cache: 'no-store' });
        if (res.ok) {
          const body = await res.json();
          const next = body?.data || {};
          if (last) {
            const changed = Object.keys(next).filter((k) => next[k] !== last[k]);
            if (changed.length) {
              listeners.current.forEach((fn) => {
                try { fn(changed); } catch (error) { console.error('[live] refresh failed:', error); }
              });
            }
          }
          last = next;
        }
      } catch (_) {
        // Offline or the API restarting — try again next tick.
      } finally {
        inFlight = false;
        schedule();
      }
    }

    const now = () => {
      if (document.hidden) { schedule(); return; }
      clearTimeout(timer);
      check();
    };
    document.addEventListener('visibilitychange', now);
    window.addEventListener('focus', now);
    window.addEventListener('online', now);
    check();
    return () => {
      stopped = true;
      clearTimeout(timer);
      document.removeEventListener('visibilitychange', now);
      window.removeEventListener('focus', now);
      window.removeEventListener('online', now);
    };
  }, [isAuthenticated]);

  return <LiveUpdatesContext.Provider value={api}>{children}</LiveUpdatesContext.Provider>;
};

/**
 * Run `onChange` whenever one of `topics` changes elsewhere.
 *
 *   useLiveRefresh(['agency'], () => load({ quiet: true }));
 *
 * `onChange` must reload WITHOUT a loading state (a spinner swaps the page out
 * and loses the scroll position) and must not reset filters or form state.
 * Always calls the latest `onChange`, so it may close over current filters.
 * Pass `enabled: false` to pause (e.g. while an unpublished draft is open).
 */
export const useLiveRefresh = (topics, onChange, { enabled = true } = {}) => {
  const ctx = useContext(LiveUpdatesContext);
  const callback = useRef(onChange);
  callback.current = onChange;
  const enabledRef = useRef(enabled);
  enabledRef.current = enabled;
  const key = (Array.isArray(topics) ? topics : [topics]).join(',');

  useEffect(() => {
    if (!ctx) return undefined;
    const wanted = key.split(',');
    return ctx.subscribe((changed) => {
      if (enabledRef.current && changed.some((t) => wanted.includes(t))) callback.current(changed);
    });
  }, [ctx, key]);
};

export default LiveUpdatesContext;
